import { withDemoContacts } from './demo-contacts';
import { emptySnapshot, type Profile, type Snapshot, type Venture } from './models';
export let CURRENT_USER = 'jamie';
export function setCurrentUser(id: string) { CURRENT_USER = id; }
export function newFounderSnapshot(profile: Profile): Snapshot {
  const s = seedSnapshot();
  s.profiles = s.profiles.map((p) =>
    p.id === CURRENT_USER ? { ...profile, id: CURRENT_USER } : p,
  );
  s.ventures = s.ventures.map((v) => ({
    ...v,
    members: v.members.filter((id) => id !== CURRENT_USER),
  }));
  s.connections = [];
  s.bookmarks = [];
  s.messages = s.messages.filter((m) => m.author !== CURRENT_USER && !m.channel.startsWith('dm:'));
  s.commitments = s.commitments.filter((c) => c.owner !== CURRENT_USER);
  s.contributions = s.contributions.filter((c) => c.contributor !== CURRENT_USER);
  s.decisions = s.decisions
    .filter((d) => d.author !== CURRENT_USER)
    .map((d) => ({
      ...d,
      participants: d.participants.filter((id) => id !== CURRENT_USER),
      votes: Object.fromEntries(Object.entries(d.votes).filter(([id]) => id !== CURRENT_USER)),
    }));
  s.events = s.events.map((e) => ({
    ...e,
    participants: e.participants.filter((id) => id !== CURRENT_USER),
  }));
  s.endorsements = s.endorsements.filter((e) => e.from !== CURRENT_USER && e.to !== CURRENT_USER);
  s.activities = [];
  return s;
}
export function seedSnapshot(now = new Date()): Snapshot {
  const s = emptySnapshot();
  const at = (days: number, hours = 10) => {
    const d = new Date(now);
    d.setUTCDate(d.getUTCDate() + days);
    d.setUTCHours(hours, 0, 0, 0);
    return d.toISOString();
  };
  const people: Array<
    [string, string, string, Profile['avatar'], string[], string[], string[], string, string]
  > = [
    [
      'jamie',
      'Jamie Reed',
      'London, UK',
      'sage',
      ['Engineering', 'Product'],
      ['Sales', 'Design'],
      ['Automotive', 'Climate'],
      'I turn rough ideas into useful products. Looking for a small team that enjoys testing things in the real world.',
      'Product engineer · 6 years',
    ],
    [
      'maya',
      'Maya Chen',
      'Bristol, UK',
      'coral',
      ['Design', 'Product'],
      ['Engineering', 'Sales'],
      ['Climate', 'Automotive'],
      'Good ideas deserve thoughtful design. I build simple experiences for messy, real-world problems.',
      'Product designer · 7 years',
    ],
    [
      'cristian',
      'Cristian Vale',
      'Bucharest, RO',
      'sky',
      ['Sales', 'Automotive', 'Networking'],
      ['Engineering', 'Design'],
      ['Automotive', 'Local services'],
      'I know independent garages inside out. Let’s make their day a little easier with something genuinely useful.',
      'B2B sales & automotive · 8 years',
    ],
    [
      'sarah',
      'Sarah Okafor',
      'London, UK',
      'lavender',
      ['Marketing', 'Content', 'Strategy'],
      ['Engineering', 'Operations'],
      ['Education', 'Creative tools'],
      'A storyteller who likes spreadsheets. I help early ideas find their first community.',
      'Growth lead · 5 years',
    ],
    [
      'alex',
      'Alex Morgan',
      'Manchester, UK',
      'sand',
      ['Operations', 'Manufacturing'],
      ['Design', 'Marketing'],
      ['Climate', 'Local services'],
      'Less waste, better systems. I’m happiest turning a promising experiment into a repeatable process.',
      'Operations manager · 9 years',
    ],
    [
      'elena',
      'Elena Marin',
      'Cluj-Napoca, RO',
      'sage',
      ['Engineering', 'Industry expertise'],
      ['Sales', 'Product'],
      ['Health & wellbeing', 'Education'],
      'Building accessible technology with people who care about the small details.',
      'Software engineer · 4 years',
    ],
    [
      'noah',
      'Noah Williams',
      'Leeds, UK',
      'sky',
      ['Finance & accounting', 'Operations'],
      ['Engineering', 'Marketing'],
      ['Food & hospitality', 'Local services'],
      'I make the numbers understandable so small teams can make better decisions.',
      'Small business accountant · 6 years',
    ],
    [
      'priya',
      'Priya Shah',
      'London, UK',
      'coral',
      ['Research', 'Product', 'Design'],
      ['Engineering', 'Sales'],
      ['Health & wellbeing', 'Climate'],
      'Curious about why people do what they do. I start with conversations, then build.',
      'User researcher · 5 years',
    ],
    [
      'leo',
      'Leo Fischer',
      'Berlin, DE',
      'lavender',
      ['Engineering', 'Content'],
      ['Marketing', 'Design'],
      ['Creative tools', 'Education'],
      'Making creative tools that get out of the way. Weekend musician, weekday builder.',
      'Creative technologist · 7 years',
    ],
    [
      'amina',
      'Amina Diallo',
      'Birmingham, UK',
      'sand',
      ['Sales', 'Marketing', 'Execution'],
      ['Engineering', 'Finance & accounting'],
      ['Food & hospitality', 'Local services'],
      'From pop-up kitchens to neighbourhood businesses. I love getting the first ten customers through the door.',
      'Community business lead · 6 years',
    ],
    [
      'ai-assistant',
      'Venture AI',
      'The Cloud',
      'sky',
      ['Frontend Engineering', 'Backend Engineering', 'UI/UX Design', 'Product Management'],
      [],
      [],
      'I am your personal AI assistant. Let me know what you want to build and I will help you map it out!',
      'AI Agent',
    ],
  ];
  s.profiles = people.map(
    ([id, name, location, avatar, skills, seeking, industries, bio, background], i) => ({
      id,
      name,
      location,
      avatar,
      skills,
      seeking,
      industries,
      bio,
      background,
      brings: skills,
      wants: seeking,
      role: id === 'jamie' ? 'Business Owner' : id === 'cristian' ? 'Both' : (i % 2 === 1 ? 'Skill Provider' : 'Both'),
      preference: i === 4 ? 'Local' : 'Remote',
      hours: i === 2 ? 12 : 8 + (i % 4),
      stage: 'Building MVP',
      style: i % 3 === 0 ? 'Regular check-ins' : 'Async first',
      commitment: 'Side project',
      idea: i % 2 ? 'Either' : 'Have an idea',
      teamSize: 3,
      looking: true,
    }),
  );
  const ventures: Array<
    Partial<Venture> &
      Pick<
        Venture,
        'id' | 'name' | 'pitch' | 'industry' | 'members' | 'seeking' | 'milestone' | 'stage'
      >
  > = [
    {
      id: 'pitstop',
      name: 'Pitstop',
      pitch: 'A calmer working day for independent garages.',
      industry: 'Automotive',
      members: ['jamie', 'cristian', 'maya'],
      seeking: ['Operations'],
      milestone: 'Test our booking prototype with 5 garages',
      stage: 'Building MVP',
      problem: 'Independent garages lose bookings between phone calls and paper diaries.',
      solution: 'One simple workspace for bookings and customer updates.',
    },
    {
      id: 'loop',
      name: 'Loop & Local',
      pitch: 'Keep useful things moving around your neighbourhood.',
      industry: 'Climate',
      members: ['alex', 'sarah'],
      seeking: ['Engineering', 'Design'],
      milestone: 'Run a repair café pilot',
      stage: 'Validating',
    },
    {
      id: 'table',
      name: 'Sunday Table',
      pitch: 'Help independent kitchens find their regulars.',
      industry: 'Food & hospitality',
      members: ['amina', 'noah'],
      seeking: ['Engineering', 'Product'],
      milestone: 'Interview 12 kitchen owners',
      stage: 'Forming Team',
    },
    {
      id: 'field',
      name: 'Fieldnotes',
      pitch: 'A little more space for your creative practice.',
      industry: 'Creative tools',
      members: ['leo', 'priya'],
      seeking: ['Marketing', 'Design'],
      milestone: 'Share an interactive prototype',
      stage: 'Exploring',
    },
    {
      id: 'sprout',
      name: 'Sprout Club',
      pitch: 'Small, practical learning groups for a greener life.',
      industry: 'Education',
      members: ['jamie', 'sarah', 'elena'],
      seeking: [],
      milestone: 'Publish the lessons from our pilot',
      stage: 'Closed',
    },
  ];
  s.ventures = ventures.map((v) => ({
    description:
      'An independent team exploring a practical problem through small experiments and honest customer conversations.',
    problem: 'People need a simpler way to get an everyday job done.',
    solution: 'Start small, learn from real people, and build the simplest useful service.',
    hours: 10,
    preference: 'Remote',
    location: 'UK & Europe',
    cadence: 'Tuesday check-in · async updates',
    createdAt: at(-30),
    ...v,
  }));
  s.connections = [
    
    {
      id: 'c1',
      from: 'jamie',
      to: 'cristian',
      status: 'accepted',
      intro: 'Let’s talk about the garage idea.',
      createdAt: at(-15),
    },
    {
      id: 'c2',
      from: 'maya',
      to: 'jamie',
      status: 'accepted',
      intro: 'I’d love to help with the prototype.',
      createdAt: at(-14),
    },
    {
      id: 'c3',
      from: 'sarah',
      to: 'jamie',
      status: 'accepted',
      intro: 'Great working together on Sprout.',
      createdAt: at(-12),
    },
    {
      id: 'c4',
      from: 'amina',
      to: 'jamie',
      status: 'pending',
      intro: 'Your product experience would be a great fit for Sunday Table. Fancy a chat?',
      createdAt: at(-1),
    },
    {
      id: 'c5',
      from: 'jamie',
      to: 'elena',
      status: 'pending',
      intro: 'I’d love to hear more about your accessible learning tools.',
      createdAt: at(-2),
    },
  ];
  s.bookmarks = [{ id: 'jamie-priya', userId: 'jamie', personId: 'priya', state: 'saved' }];
  s.messages = [
    {
      id: 'm1',
      channel: 'pitstop',
      author: 'cristian',
      text: 'Spoke to two more garage owners today. Both lose time chasing booking confirmations.',
      reactions: ['jamie'],
      createdAt: at(-1, 9),
    },
    {
      id: 'm2',
      channel: 'pitstop',
      author: 'maya',
      text: 'That’s helpful. I’ve simplified the booking flow to three steps. Ready for a quick review?',
      reactions: [],
      createdAt: at(-1, 10),
    },
    {
      id: 'm3',
      channel: 'pitstop',
      author: 'jamie',
      text: 'Yes! I’ll connect the prototype before our Tuesday check-in.',
      reference: 'Booking prototype',
      reactions: ['maya'],
      createdAt: at(-1, 11),
    },
    {
      id: 'm4',
      channel: 'dm:cristian:jamie',
      author: 'cristian',
      text: 'I have a garage owner who’s happy to try Pitstop next week. Shall we put 20 minutes in the diary?',
      reactions: [],
      createdAt: at(-1, 13),
    },
  ];
  s.decisions = [
    {
      id: 'd1',
      ventureId: 'pitstop',
      author: 'cristian',
      title: 'Who should we test with first?',
      context: 'Let’s focus our first five interviews on one customer group.',
      options: ['Independent garages', 'Dealerships', 'Mobile mechanics'],
      deadline: at(4, 18),
      participants: ['jamie', 'cristian', 'maya'],
      votes: { cristian: 0, maya: 0 },
      comments: [],
      createdAt: at(-2),
    },
    {
      id: 'd2',
      ventureId: 'pitstop',
      author: 'jamie',
      title: 'How often should we meet?',
      context: 'Keep the time useful and leave space for focused work.',
      options: ['Weekly, 25 minutes', 'Twice a week, 15 minutes'],
      deadline: at(-8),
      participants: ['jamie', 'cristian', 'maya'],
      votes: { jamie: 0, cristian: 0, maya: 0 },
      comments: [],
      closedAt: at(-8),
      createdAt: at(-10),
    },
  ];
  s.commitments = [
    {
      id: 't1',
      ventureId: 'pitstop',
      title: 'Connect the booking prototype',
      description: 'Make the happy path work from selecting a time to a confirmation.',
      owner: 'jamie',
      dueDate: at(3).slice(0, 10),
      priority: 'High',
      status: 'In Progress',
      effort: 6,
      milestone: 'Five garage pilot',
      comments: [],
      createdAt: at(-3),
    },
    {
      id: 't2',
      ventureId: 'pitstop',
      title: 'Interview 5 garage owners',
      description: 'Ask about their current booking process and biggest frustrations.',
      owner: 'cristian',
      dueDate: at(5).slice(0, 10),
      priority: 'High',
      status: 'In Progress',
      effort: 4,
      milestone: 'Five garage pilot',
      comments: [],
      createdAt: at(-3),
    },
    {
      id: 't3',
      ventureId: 'pitstop',
      title: 'Prototype wireframes',
      description: 'Three screens, one clear booking flow.',
      owner: 'maya',
      dueDate: at(-1).slice(0, 10),
      priority: 'Medium',
      status: 'Completed',
      effort: 5,
      milestone: 'Five garage pilot',
      comments: [],
      completedAt: at(-2),
      createdAt: at(-7),
    },
    {
      id: 't4',
      ventureId: 'pitstop',
      title: 'Review customer consent wording',
      description: 'Waiting for feedback from the pilot garages.',
      owner: 'jamie',
      dueDate: at(-1).slice(0, 10),
      priority: 'Medium',
      status: 'Cancelled',
      effort: 2,
      milestone: 'Five garage pilot',
      comments: [],
      createdAt: at(-5),
    },
    {
      id: 't5',
      ventureId: 'sprout',
      title: 'Publish our pilot learnings',
      description: 'Record what we learned for the next team.',
      owner: 'jamie',
      dueDate: at(-12).slice(0, 10),
      priority: 'Medium',
      status: 'Completed',
      effort: 3,
      milestone: 'Close the pilot thoughtfully',
      comments: [],
      completedAt: at(-13),
      createdAt: at(-20),
    },
    {
      id: 't6',
      ventureId: 'pitstop',
      title: 'Write the pilot welcome note',
      description: 'A friendly introduction for the first five garages.',
      owner: 'maya',
      dueDate: at(6).slice(0, 10),
      priority: 'Low',
      status: 'In Progress',
      effort: 1,
      milestone: 'Five garage pilot',
      comments: [],
      createdAt: at(-1),
    },
  ];
  s.events = [
    {
      id: 'e1',
      ventureId: 'pitstop',
      title: 'Tuesday team check-in',
      type: 'Meeting',
      startsAt: at(2, 17),
      details: '25 minutes. Prototype walkthrough, interview notes, and next steps.',
      participants: ['jamie', 'cristian', 'maya'],
    },
    {
      id: 'e2',
      ventureId: 'pitstop',
      title: 'First garage pilot',
      type: 'Milestone',
      startsAt: at(7, 9),
      details: 'Test the booking flow with five independent garages.',
      participants: ['jamie', 'cristian', 'maya'],
    },
  ];
  s.contributions = [
    {
      id: 'l1',
      ventureId: 'pitstop',
      contributor: 'maya',
      type: 'Work delivered',
      title: 'Booking flow wireframes',
      description: 'Designed the three-step booking flow.',
      currency: 'GBP',
      date: at(-2).slice(0, 10),
      commitmentId: 't3',
      evidence: 'Reviewed together in our check-in.',
      createdAt: at(-2),
    },
    {
      id: 'l2',
      ventureId: 'pitstop',
      contributor: 'cristian',
      type: 'Customer interview',
      title: 'Four conversations with garage owners',
      description: 'Documented booking pain points and pilot interest.',
      quantity: 4,
      currency: 'GBP',
      date: at(-1).slice(0, 10),
      evidence: 'Interview notes shared with the team.',
      createdAt: at(-1),
    },
    {
      id: 'l3',
      ventureId: 'pitstop',
      contributor: 'jamie',
      type: 'Time',
      title: 'Built the first prototype',
      description: 'A working starting point for customer tests.',
      quantity: 8,
      currency: 'GBP',
      date: at(-3).slice(0, 10),
      evidence: 'Local prototype demonstrated to the team.',
      createdAt: at(-3),
    },
    {
      id: 'l4',
      ventureId: 'pitstop',
      contributor: 'maya',
      type: 'Expense paid',
      title: 'Domain and design resources',
      description: 'Historical out-of-pocket expense. No payment is made by the app.',
      expenseMinor: 3820,
      currency: 'GBP',
      date: at(-4).slice(0, 10),
      evidence: 'Receipt recorded by Maya.',
      createdAt: at(-4),
    },
    {
      id: 'l5',
      ventureId: 'sprout',
      contributor: 'jamie',
      type: 'Research',
      title: 'Lessons from the Sprout pilot',
      description: 'The pilot closed, but the learning remains useful.',
      currency: 'GBP',
      date: at(-13).slice(0, 10),
      evidence: 'Team retrospective.',
      createdAt: at(-13),
    },
  ];
  s.endorsements = [
    {
      id: 'en1',
      from: 'sarah',
      to: 'jamie',
      skill: 'Engineering',
      text: 'Made complicated ideas easy to test. Thoughtful and dependable throughout our pilot.',
      ventureId: 'sprout',
    },
  ];
  s.activities = [
    {
      id: 'a1',
      ventureId: 'pitstop',
      title: 'Maya completed Prototype wireframes',
      detail: 'A clearer booking experience is ready to try.',
      kind: 'commitment',
      createdAt: at(-2),
    },
    {
      id: 'a2',
      ventureId: 'pitstop',
      title: 'Cristian added 4 customer interviews',
      detail: 'New learning for the garage pilot.',
      kind: 'contribution',
      createdAt: at(-1),
    },
    {
      id: 'a3',
      title: 'Amina sent you a connection request',
      detail: 'A conversation about Sunday Table.',
      kind: 'connection',
      createdAt: at(-1, 14),
    },
    {
      id: 'a4',
      ventureId: 'pitstop',
      title: 'A decision needs your perspective',
      detail: 'Who should we test with first?',
      kind: 'decision',
      createdAt: at(0, 8),
    },
    {
      id: 'a5',
      ventureId: 'pitstop',
      title: 'Your next team check-in',
      detail: 'Review the booking prototype together.',
      kind: 'meeting',
      createdAt: at(0, 9),
    },
  ];
  return withDemoContacts(s, now);
}
